import { stripBroadcastAttempts } from "./broadcasts";
import type { PingKind } from "./types";

/**
 * Slack-style mrkdwn <-> HTML core.
 *
 * mrkdwn is the single source of truth for every text value in the app. This
 * module renders mrkdwn into the HTML that contenteditable editors and previews
 * display, and serializes an editor DOM back into mrkdwn. It is deliberately
 * free of browser APIs so both the client and the Bun server can use it.
 */

export type MrkdwnMode = "rich" | "mrkdwn" | "emoji";

export type MrkdwnRenderOptions = {
  /** When set, `{{BCHANNEL_BROADCAST:*}}` and `<!channel>` tokens become ping spans. */
  ping?: PingKind;
  /** `emoji` renders plain text + emoji only; everything else renders full mrkdwn. */
  mode?: MrkdwnMode;
  /** Multiline renders block elements; single-line renders inline content only. */
  multiline?: boolean;
  /** Resolves `:name:` to an image URL. Unknown names stay literal. */
  emojiUrl?: (name: string) => string | null | undefined;
  /** Resolves a standard emoji shortcode to its Unicode glyph. */
  emojiUnicode?: (name: string) => string | null | undefined;
};

export type MrkdwnSerializeOptions = {
  /** Single-line fields serialize as one line; multiline uses block separators. */
  multiline?: boolean;
};

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

/** Minimal structural view of a DOM or node-html-parser tree. */
export type MrkdwnTreeNode = {
  nodeType: number;
  childNodes?: readonly MrkdwnTreeNode[];
  textContent?: string;
  tagName?: string;
  getAttribute?: (name: string) => string | null | undefined;
};

function children(node: MrkdwnTreeNode): MrkdwnTreeNode[] {
  return Array.from(node.childNodes ?? []);
}

function tag(node: MrkdwnTreeNode): string {
  return node.tagName ? String(node.tagName).toLowerCase() : "";
}

function attr(node: MrkdwnTreeNode, name: string): string | null {
  const value = node.getAttribute?.(name);
  return value === undefined ? null : value;
}

export function escapeMrkdwnText(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

export function cleanInlineText(value: string) {
  return value
    .replaceAll("\u200b", "")
    .replaceAll("\uFEFF", "")
    .replaceAll("\u00a0", " ")
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n");
}

/** Places formatting markers around content while keeping boundary whitespace outside. */
export function wrapSlackStyle(value: string, marker: string) {
  const match = value.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match) return value;
  const [, leading = "", content = "", trailing = ""] = match;
  if (!content) return value;
  return `${leading}${marker}${content}${marker}${trailing}`;
}
const NATIVE_BROADCAST = /<!(channel|here)(?:\|[^>]*)?>/gi;
const USERGROUP_MENTION = /<!subteam\^([A-Z0-9]+)(?:\|([^>]+))?>/gi;

/** mrkdwn for a Slack user-group mention; Slack resolves the handle itself. */
export function userGroupMentionMrkdwn(groupId: string, label: string) {
  return `<!subteam^${groupId}${label ? `|${label}` : ""}>`;
}

/** Converts typed broadcast spellings into the movable internal marker. */
export function normalizeBroadcastMrkdwn(value: string, ping: PingKind) {
  if (ping === "none") return stripBroadcastAttempts(value);
  return value
    .replace(NATIVE_BROADCAST, (_match, kind: string) =>
      kind.toLowerCase() === "here" ? "{{BCHANNEL_BROADCAST:here}}" : "{{BCHANNEL_BROADCAST:channel}}",
    )
    .replace(/(^|[^\w])@(channel|here)\b/gi, (_match, prefix: string, kind: string) => {
      const normalized = kind.toLowerCase() === "here" ? "here" : "channel";
      return `${prefix}{{BCHANNEL_BROADCAST:${normalized}}}`;
    });
}

export function broadcastMarkerMrkdwn(kind: "channel" | "here") {
  return `{{BCHANNEL_BROADCAST:${kind}}}`;
}

export function mentionHtml(className: string, attribute: string, label: string, atomic = true) {
  const editable = atomic ? ' contenteditable="false"' : "";
  return `<span class="${className}" ${attribute}${editable}>${escapeMrkdwnText(label)}</span>`;
}

export function emojiImgHtml(name: string, url: string) {
  return `<img class="inline-custom-emoji" data-emoji-name="${name}" src="${escapeMrkdwnText(url)}" alt=":${name}:">`;
}

export function emojiGlyphHtml(name: string, unicode: string) {
  return `<span class="inline-emoji-glyph" data-emoji-name="${name}" aria-label=":${name}:">${escapeMrkdwnText(unicode)}</span>`;
}

function inlineCodeHtml(code: string) {
  return `<code>${escapeMrkdwnText(code)}</code>`;
}

/**
 * Renders the emphasis markers (* bold, _ italic, __ underline, ~ strike) with
 * one level of nesting. Unmatched markers are kept literal.
 */
function renderEmphasis(text: string) {
  const stack: Array<{ marker: string | null; html: string }> = [{ marker: null, html: "" }];
  const isSpace = (char: string | undefined) => char !== undefined && /\s/.test(char);
  const opensAfter = (char: string | undefined) => char === undefined || isSpace(char) || "([*_~`\"'".includes(char);
  let i = 0;
  const topFrame = () => stack[stack.length - 1] as { marker: string | null; html: string };
  while (i < text.length) {
    const char = text[i] ?? "";
    let marker: string | null = null;
    let length = 0;
    if (char === "*") {
      marker = "*";
      length = 1;
    } else if (char === "~") {
      marker = "~";
      length = 1;
    } else if (char === "_") {
      marker = text[i + 1] === "_" ? "__" : "_";
      length = marker === "__" ? 2 : 1;
    }
    if (marker) {
      const prev = i > 0 ? text[i - 1] : undefined;
      const next = i + length < text.length ? text[i + length] : undefined;
      const top = topFrame();
      const canOpen = opensAfter(prev) && next !== undefined && !isSpace(next) && stack.length < 5;
      const canClose = prev !== undefined && !isSpace(prev) && top.marker === marker;
      if (canOpen) {
        stack.push({ marker, html: "" });
        i += length;
        continue;
      }
      if (canClose) {
        const frame = stack.pop();
        if (!frame) continue;
        if (frame.html) {
          const tag = tagForMarker(frame.marker);
          topFrame().html += `<${tag}>${frame.html}</${tag}>`;
        } else {
          // Empty markers like `**` stay literal (Slack behavior).
          topFrame().html += `${frame.marker ?? ""}${marker}`;
        }
        i += length;
        continue;
      }
      topFrame().html += text.slice(i, i + length);
      i += length;
      continue;
    }
    topFrame().html += char;
    i += 1;
  }
  let html = topFrame().html;
  const last = stack[stack.length - 1];
  if (last?.marker) html = last.marker + html;
  for (let depth = stack.length - 2; depth >= 0; depth -= 1) {
    const frame = stack[depth];
    if (frame) html = (frame.marker ?? "") + frame.html + html;
  }
  return html;
}

function tagForMarker(marker: string | null) {
  if (marker === "__") return "u";
  if (marker === "~") return "s";
  if (marker === "_") return "em";
  return "strong";
}

/** Renders one paragraph's worth of inline content into escaped HTML. */
function renderInline(value: string, options: MrkdwnRenderOptions) {
  const { ping = "none", mode = "rich", emojiUrl, emojiUnicode } = options;
  const protectedHtml: string[] = [];
  const protect = (html: string) => {
    const token = `\uE000${protectedHtml.length}\uE001`;
    protectedHtml.push(html);
    return token;
  };

  let text = value;

  if (mode !== "emoji") {
    text = text.replace(/`([^`\n]+)`/g, (_match, code: string) => protect(inlineCodeHtml(code)));
    text = text.replace(/<@([UW][A-Z0-9]+)(?:\|([^>]+))?>/gi, (_match, id: string, label?: string) => {
      const normalized = id.toUpperCase();
      return protect(mentionHtml("mention", `data-user-id="${normalized}"`, `@${label || normalized}`));
    });
    text = text.replace(/<#([CG][A-Z0-9]+)(?:\|([^>]+))?>/gi, (_match, id: string, label?: string) => {
      const normalized = id.toUpperCase();
      return protect(mentionHtml("mention channel", `data-channel-id="${normalized}"`, `#${label || normalized}`));
    });
    text = text.replace(/\{\{BCHANNEL_BROADCAST:(channel|here)\}\}(\s*)/gi, (_match, kind: string, trailing: string) =>
      ping === "none" ? "" : `${protect(mentionHtml("mention ping", `data-ping="${kind}"`, `@${kind}`))}${trailing}`,
    );
    text = text.replace(USERGROUP_MENTION, (_match, id: string, label?: string) =>
      protect(mentionHtml("mention group", `data-group-id="${id}"`, `@${label || id}`)),
    );
    text = text.replace(/<(https?:\/\/[^|>\s]+)(?:\|([^>]+))?>/gi, (_match, url: string, label?: string) =>
      protect(`<a href="${escapeMrkdwnText(url)}">${escapeMrkdwnText(label || url)}</a>`),
    );
    text = text.replace(/(^|[\s(>])(https?:\/\/[^\s<]+)(?=[\s<])/gi, (match, prefix: string, raw: string) => {
      const trailing = (raw.match(/[.,;:!?)\]»]+$/) ?? [""])[0];
      const url = raw.slice(0, raw.length - trailing.length);
      if (!url) return match;
      return `${prefix}${protect(`<a href="${escapeMrkdwnText(url)}">${escapeMrkdwnText(url)}</a>`)}${trailing}`;
    });
  }

  text = text.replace(/:([a-z0-9_+-]{1,100}):/gi, (match, rawName: string) => {
    const name = rawName.toLowerCase();
    const url = emojiUrl?.(name);
    if (url) return protect(emojiImgHtml(name, url));
    const unicode = emojiUnicode?.(name);
    return unicode ? protect(emojiGlyphHtml(name, unicode)) : match;
  });

  text = escapeMrkdwnText(text);
  if (mode !== "emoji") text = renderEmphasis(text);
  return text.replace(/\uE000(\d+)\uE001/g, (_token, index: string) => protectedHtml[Number(index)] ?? "");
}

type ListItem = { level: number; ordered: boolean; content: string };

/** Parses a run of list lines (with 2-space indent per nesting level) into nested HTML. */
function parseListBlock(lines: string[], start: number, options: MrkdwnRenderOptions) {
  const items: ListItem[] = [];
  let i = start;
  while (i < lines.length) {
    const match = lines[i]?.match(/^(\s*)(?:([-*•])|(\d+)\.)\s+(.*)$/);
    if (!match) break;
    items.push({
      level: Math.floor((match[1] ?? "").length / 2),
      ordered: match[3] !== undefined,
      content: renderInline(match[4] ?? "", options),
    });
    i += 1;
  }
  return { html: buildListHtml(items, 0).html, next: i };
}

function buildListHtml(items: ListItem[], index: number): { html: string; next: number } {
  const first = items[index];
  const tag = first?.ordered ? "ol" : "ul";
  const level = first?.level ?? 0;
  const lis: string[] = [];
  let i = index;
  while (i < items.length && items[i]?.level === level) {
    let inner = items[i]?.content ?? "";
    i += 1;
    if (items[i] && (items[i]?.level ?? 0) > level) {
      const nested = buildListHtml(items, i);
      inner += nested.html;
      i = nested.next;
    }
    lis.push(`<li>${inner}</li>`);
  }
  return { html: `<${tag}>${lis.join("")}</${tag}>`, next: i };
}

/**
 * Renders Slack-style mrkdwn into clean HTML. When `multiline` is false the
 * output is inline content only, suitable for single-line fields.
 */
export function renderMrkdwn(value: string, options: MrkdwnRenderOptions = {}): string {
  const { multiline = true } = options;
  const source = cleanInlineText(value);

  if (!multiline) {
    return renderInline(source.replace(/\s*\n+\s*/g, " "), options);
  }

  const lines = source.split("\n");
  const blocks: string[] = [];
  let paragraph: string[] = [];
  const flushParagraph = () => {
    if (!paragraph.length) return;
    const text = renderInline(paragraph.join("\n"), options).replace(/\n/g, "<br>");
    paragraph = [];
    blocks.push(text ? `<p>${text}</p>` : "<p><br></p>");
  };

  let i = 0;
  const at = (index: number) => lines[index] ?? "";
  while (i < lines.length) {
    const line = at(i);
    if (/^\s*```/.test(line)) {
      flushParagraph();
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !/^\s*```/.test(at(i))) {
        code.push(at(i));
        i += 1;
      }
      i += 1;
      blocks.push(`<pre>${escapeMrkdwnText(code.join("\n"))}</pre>`);
      continue;
    }
    if (/^>\s?/.test(line)) {
      flushParagraph();
      const content: string[] = [];
      while (i < lines.length && /^>\s?/.test(at(i))) {
        content.push(at(i).replace(/^>\s?/, ""));
        i += 1;
      }
      const text = renderInline(content.join("\n"), options).replace(/\n/g, "<br>");
      blocks.push(`<blockquote>${text}</blockquote>`);
      continue;
    }
    const listStart = line.match(/^(\s*)(?:[-*•]|\d+\.)\s+/);
    if (listStart) {
      flushParagraph();
      const parsed = parseListBlock(lines, i, options);
      blocks.push(parsed.html);
      i = parsed.next;
      continue;
    }
    if (line.trim() === "") {
      flushParagraph();
      let blanks = 0;
      while (i < lines.length && at(i).trim() === "") {
        blanks += 1;
        i += 1;
      }
      for (let empty = 0; empty < Math.floor(blanks / 2); empty += 1) blocks.push("<p><br></p>");
      continue;
    }
    paragraph.push(line);
    i += 1;
  }
  flushParagraph();

  if (!blocks.length) blocks.push("<p><br></p>");
  return blocks.join("");
}

function serializeInline(node: MrkdwnTreeNode): string {
  if (node.nodeType === TEXT_NODE) return cleanInlineText(node.textContent ?? "");
  if (node.nodeType !== ELEMENT_NODE) return "";
  const nodeTag = tag(node);
  if (nodeTag === "br") return "\n";
  if ((attr(node, "class") ?? "").split(/\s+/).includes("ql-ui")) return "";
  // Nested lists are serialized at block level, not inline.
  if (nodeTag === "ul" || nodeTag === "ol") return "";

  const ping = attr(node, "data-ping");
  if (ping === "channel" || ping === "here") {
    const text = cleanInlineText(node.textContent ?? "").trim();
    if (text === `@${ping}`) return broadcastMarkerMrkdwn(ping);
    // Edited (e.g. "@" deleted): the token degrades back to plain text.
    return text;
  }
  if (ping) return "";

  const groupId = attr(node, "data-group-id");
  if (groupId && /^[A-Z0-9]+$/.test(groupId)) {
    const label = cleanInlineText(node.textContent ?? "")
      .replace(/^@/, "")
      .trim();
    return label && label !== groupId
      ? userGroupMentionMrkdwn(groupId.toUpperCase(), label)
      : `<!subteam^${groupId.toUpperCase()}>`;
  }

  const emojiName = attr(node, "data-emoji-name");
  if (emojiName && /^[a-z0-9_+-]{1,100}$/i.test(emojiName)) return `:${emojiName.toLowerCase()}:`;

  const userId = attr(node, "data-user-id");
  if (userId && /^U[A-Z0-9]+$/.test(userId)) {
    const label = cleanInlineText(node.textContent ?? "")
      .replace(/^@/, "")
      .trim();
    return label && label !== userId ? `<@${userId}|${label}>` : `<@${userId}>`;
  }

  const channelId = attr(node, "data-channel-id");
  if (channelId && /^[CG][A-Z0-9]+$/.test(channelId)) {
    const label = cleanInlineText(node.textContent ?? "")
      .replace(/^#/, "")
      .trim();
    return label && label !== channelId ? `<#${channelId}|${label}>` : `<#${channelId}>`;
  }

  const inside = children(node).map(serializeInline).join("");

  if (nodeTag === "a") {
    const href = attr(node, "href") ?? "";
    if (!/^https?:\/\//i.test(href)) return inside;
    const label = inside.trim();
    return label && label !== href ? `<${href}|${label}>` : `<${href}>`;
  }
  if (nodeTag === "strong" || nodeTag === "b") return wrapSlackStyle(inside, "*");
  if (nodeTag === "em" || nodeTag === "i") return wrapSlackStyle(inside, "_");
  if (nodeTag === "u") return wrapSlackStyle(inside, "__");
  if (nodeTag === "s" || nodeTag === "strike") return wrapSlackStyle(inside, "~");
  if (nodeTag === "code") return wrapSlackStyle(inside, "`");
  return inside;
}

/** Serializes a list (and its nested lists) into indented mrkdwn lines. */
function serializeListLines(node: MrkdwnTreeNode, ordered: boolean, level: number, lines: string[]) {
  const indent = "  ".repeat(level);
  let ordinal = 1;
  for (const child of children(node)) {
    if (child.nodeType !== ELEMENT_NODE || tag(child) !== "li") continue;
    const nestedList = children(child).find((c) => c.nodeType === ELEMENT_NODE && (tag(c) === "ul" || tag(c) === "ol"));
    const inline = serializeInline(child).trim().replace(/\n/g, " ");
    lines.push(`${indent}${ordered ? `${ordinal}. ` : "- "}${inline}`);
    ordinal += 1;
    if (nestedList) serializeListLines(nestedList, tag(nestedList) === "ol", level + 1, lines);
  }
}

/** Quill keeps mixed and nested list items flat, with type/indent attributes on each item. */
function serializeQuillListLines(node: MrkdwnTreeNode, lines: string[]) {
  const ordinals = new Map<number, number>();
  for (const child of children(node)) {
    if (child.nodeType !== ELEMENT_NODE || tag(child) !== "li") continue;
    const list = attr(child, "data-list");
    const ordered = list === "ordered";
    const className = attr(child, "class") ?? "";
    const indent = Number(className.match(/(?:^|\s)ql-indent-(\d+)(?:\s|$)/)?.[1] ?? 0);
    const inline = serializeInline(child).trim().replace(/\n/g, " ");
    const ordinal = ordinals.get(indent) ?? 1;
    lines.push(`${"  ".repeat(indent)}${ordered ? `${ordinal}. ` : "- "}${inline}`);
    if (ordered) ordinals.set(indent, ordinal + 1);
    else ordinals.delete(indent);
    for (const level of ordinals.keys()) {
      if (level > indent) ordinals.delete(level);
    }
  }
}

function serializeBlock(node: MrkdwnTreeNode, blocks: string[]) {
  if (node.nodeType === TEXT_NODE) {
    const text = cleanInlineText(node.textContent ?? "").trim();
    if (text) blocks.push(text);
    return;
  }
  if (node.nodeType !== ELEMENT_NODE) return;
  const nodeTag = tag(node);
  const className = attr(node, "class") ?? "";

  if (className.split(/\s+/).includes("ql-code-block-container")) {
    const code = children(node)
      .filter((child) => (attr(child, "class") ?? "").split(/\s+/).includes("ql-code-block"))
      .map((child) => cleanInlineText(child.textContent ?? ""))
      .join("\n");
    blocks.push(`\`\`\`\n${code}\n\`\`\``);
    return;
  }

  if (nodeTag === "pre") {
    blocks.push(`\`\`\`\n${node.textContent ?? ""}\n\`\`\``);
    return;
  }
  if (nodeTag === "blockquote") {
    const inner = serializeInline(node).trim();
    if (!inner) return;
    blocks.push(
      inner
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n"),
    );
    return;
  }
  if (nodeTag === "ul" || nodeTag === "ol") {
    const lines: string[] = [];
    const quillList = children(node).some(
      (child) => child.nodeType === ELEMENT_NODE && tag(child) === "li" && attr(child, "data-list") !== null,
    );
    if (quillList) serializeQuillListLines(node, lines);
    else serializeListLines(node, nodeTag === "ol", 0, lines);
    if (!lines.length) return;
    blocks.push(lines.join("\n"));
    return;
  }
  if (nodeTag === "li") {
    const text = serializeInline(node).trim();
    if (text) blocks.push(`- ${text}`);
    return;
  }

  // Keep spaces the author typed; only drop the newlines that a bare <br>
  // placeholder contributes so an empty paragraph still round-trips.
  const text = serializeInline(node).replace(/^\n+/, "").replace(/\n+$/, "");
  blocks.push(text);
}

/** Serializes an editor DOM back into mrkdwn. Works with real DOM nodes or node-html-parser. */
export function serializeMrkdwn(root: MrkdwnTreeNode, options: MrkdwnSerializeOptions = {}): string {
  const { multiline = true } = options;
  if (!multiline) {
    return serializeInline(root).replace(/\s+/g, " ").trim();
  }
  const blocks: string[] = [];
  for (const child of children(root)) serializeBlock(child, blocks);
  // Quill represents every visual line as a sibling block. Empty sibling
  // blocks carry paragraph spacing, so joining with one newline preserves both
  // soft line breaks and intentional blank lines.
  if ((attr(root, "class") ?? "").split(/\s+/).includes("ql-editor")) return blocks.join("\n");
  // A document that is entirely empty paragraphs (e.g. Enter pressed on an
  // empty composer) needs a canonical form that renders back to the same count:
  // N empties are `""` for N=1, otherwise `"\n" + "\n\n".repeat(N-1)` (2N-1
  // newlines). The normal `\n\n` join below can't represent consecutive empties.
  if (blocks.every((block) => block === "")) {
    if (blocks.length <= 1) return "";
    return `\n${"\n\n".repeat(blocks.length - 1)}`;
  }
  return blocks.join("\n\n");
}

/** Extracts user and channel IDs referenced by a mrkdwn string. */
export function extractMentionIds(value: string) {
  const userIds = [
    ...new Set(
      Array.from(value.matchAll(/<@([UW][A-Z0-9]+)(?:\|[^>]+)?>/gi), (match) => match[1] ?? "").filter(Boolean),
    ),
  ];
  const channelIds = [
    ...new Set(
      Array.from(value.matchAll(/<#([CG][A-Z0-9]+)(?:\|[^>]+)?>/gi), (match) => match[1] ?? "").filter(Boolean),
    ),
  ];
  return { userIds, channelIds };
}

/** Replaces bare mention IDs with their display labels in a mrkdwn string. */
export function labelMentions(
  value: string,
  users: Map<string, string>,
  channels: Map<string, string>,
  options: { mode?: MrkdwnMode } = {},
) {
  const { mode = "rich" } = options;
  if (mode === "emoji") return value;
  let result = value.replace(/<@([UW][A-Z0-9]+)(?:\|([^>]+))?>/gi, (_match, id: string, label?: string) => {
    const name = label && label !== id ? label : users.get(id.toUpperCase());
    return `<@${id.toUpperCase()}${name ? `|${name}` : ""}>`;
  });
  result = result.replace(/<#([CG][A-Z0-9]+)(?:\|([^>]+))?>/gi, (_match, id: string, label?: string) => {
    const name = label && label !== id ? label : channels.get(id.toUpperCase());
    return `<#${id.toUpperCase()}${name ? `|${name}` : ""}>`;
  });
  return result;
}

/** Removes emphasis markers, inline code, and link syntax from a mrkdwn range. */
export function stripInlineFormatting(value: string) {
  return value
    .replace(/`([^`\n]+)`/g, "$1")
    .replace(/```(?:[^\n`]*)\n?([\s\S]*?)```/g, "$1")
    .replace(USERGROUP_MENTION, (_match, _id: string, label?: string) => `@${label || _id}`)
    .replace(/<([^|>]+)\|([^>]+)>/g, "$2")
    .replace(/<(https?:\/\/[^>]+)>/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/__([^_\n]+)__/g, "$1")
    .replace(/_([^_\n]+)_/g, "$1")
    .replace(/~([^~\n]+)~/g, "$1");
}

/**
 * Wraps bare `http(s)` URLs in pasted text as `<url>` so they render as links
 * immediately. The inline renderer only auto-links a URL when it is followed
 * by whitespace, so a URL pasted at the end of a line stays plain until a
 * space is typed; wrapping it here fixes that without linking mid-typing.
 */
export function autolinkPastedText(value: string) {
  return value.replace(/(^|[\s(>])(https?:\/\/[^\s<]+)(?=$|[\s<])/gi, (_match, prefix: string, raw: string) => {
    const trailing = (raw.match(/[.,;:!?)\]»]+$/) ?? [""])[0];
    const url = raw.slice(0, raw.length - trailing.length);
    if (!url) return _match;
    return `${prefix}<${url}>${trailing}`;
  });
}
